import {createAudioContext} from "./TextToSpeechUtilsPoly";
import {Queue} from "../contexts/Queue";

//AudioPlayer receives audio chunks and queues them for playback on a device
//Its whole purpose is to encapsulate logic related to the Web Audio API
export class AudioPlayer {
    private audioContext: AudioContext;
    private currentGainNode: GainNode;
    private audioDeviceId: string;
    private audioBufferSourceNode: AudioBufferSourceNode | null = null;
    private pendingSpeakerQueue: Queue<AudioBuffer>;
    private imSpeaking: boolean = false;
    private pollingInterval: NodeJS.Timer | undefined = undefined;
    private gain: number = 0.5
    private isStopped: boolean = false;

    constructor(deviceId: string, initialGain: number = 0.5) {
        this.audioDeviceId = deviceId;
        this.audioContext = createAudioContext(deviceId) as AudioContext;
        this.currentGainNode = this.audioContext.createGain();
        this.gain = initialGain;
        this.currentGainNode.gain.setValueAtTime(this.gain, this.audioContext.currentTime);
        this.pendingSpeakerQueue = new Queue<AudioBuffer>();
    }

    public start(): void {
        this.isStopped = false;
        if (this.pollingInterval) {
            throw new Error("[AudioPlayer] Already running.");
        }

        this.pollingInterval = setInterval(() => {
            if (this.imSpeaking) return;

            const audioToSpeak = this.pendingSpeakerQueue.dequeue();
            if (audioToSpeak) this.playOnDevice(audioToSpeak);
        }, 100);
    }

    public stop(): void {
        clearInterval(this.pollingInterval);
        this.pollingInterval = undefined;
        this.isStopped = true;

        try {
            if (this.audioBufferSourceNode) {
                this.audioBufferSourceNode.onended = null;
                this.audioBufferSourceNode.stop();
                this.audioBufferSourceNode.disconnect();
                this.audioBufferSourceNode = null;
            }
        } catch {
        }

        try {
            this.currentGainNode?.disconnect();
        } catch {
        }

        this.imSpeaking = false;
        try {
            this.audioContext.close();
        } catch {
        }
        console.info("AudioPlayer Stopped")
    }

    public play(rawAudioChunk: ArrayBuffer): void {
        console.info("[AudioPlayer] Playing... ", rawAudioChunk.byteLength);
        this.audioContext.decodeAudioData(rawAudioChunk)
            .then((audioBuffer) => this.pendingSpeakerQueue.enqueue(audioBuffer))
            .catch((error) => console.log('[Text2SpeechProcessor] err', error));
    }

    public playing(): boolean {
        return this.imSpeaking
    }

    public setVolume(gain: number): void {
        this.gain = gain;
        if (this.currentGainNode) {
            this.currentGainNode.gain.setValueAtTime(this.gain, this.audioContext.currentTime);
        }
    }

    public setOutputDevice(deviceId: string, force: boolean = false): void {
        if (!deviceId || (deviceId === this.audioDeviceId && !force) || this.isStopped) {
            return;
        }

        // Prefer in-place sink switch when supported (Edge/Chrome),
        // which avoids creating a fresh AudioContext that can remain suspended.
        const contextWithSink = this.audioContext as AudioContext & {
            setSinkId?: (sinkId: string) => Promise<void>;
        };

        if (typeof contextWithSink.setSinkId === 'function') {
            contextWithSink.setSinkId(deviceId)
                .then(async () => {
                    this.audioDeviceId = deviceId;
                    await this.resumeContext();
                })
                .catch(async () => {
                    await this.recreateContext(deviceId);
                });
            return;
        }

        void this.recreateContext(deviceId);
    }

    /**
     * Tears down and rebuilds the playback AudioContext on the current device.
     *
     * Needed after the session's microphone capture is re-acquired (the Two-Way AEC
     * toggle): the echo canceller of a NEW capture does not pick up playout streams
     * that already existed, so a long-lived TTS context plays audio the canceller
     * never receives as reference — the mic re-hears TTS as if AEC were off, and
     * track.getSettings() still reports echoCancellation: true. Recreating the
     * context after the new capture exists registers the playout stream with the
     * fresh canceller, which is what a full session restart does implicitly
     * (empirically confirmed to clear the feedback loop without a page reload).
     */
    public resetOutputContext(): void {
        if (this.isStopped) {
            return;
        }
        void this.recreateContext(this.audioDeviceId);
    }

    private async resumeContext(): Promise<void> {
        if (this.audioContext.state === 'suspended') {
            try {
                await this.audioContext.resume();
            } catch {
            }
        }
    }

    private async recreateContext(deviceId: string): Promise<void> {
        try {
            if (this.audioBufferSourceNode) {
                this.audioBufferSourceNode.onended = null;
                this.audioBufferSourceNode.stop();
                this.audioBufferSourceNode.disconnect();
                this.audioBufferSourceNode = null;
            }
        } catch {
        }

        try {
            this.currentGainNode?.disconnect();
        } catch {
        }

        try {
            this.audioContext?.close();
        } catch {
        }

        this.audioDeviceId = deviceId;
        this.audioContext = createAudioContext(deviceId) as AudioContext;
        this.currentGainNode = this.audioContext.createGain();
        this.currentGainNode.gain.setValueAtTime(this.gain, this.audioContext.currentTime);
        this.imSpeaking = false;
        await this.resumeContext();
    }

    // public getAudioSource() : AudioNode {
    //     return this.currentGainNode
    // }

    // public getAudioSource() { return this.sou }             // pre-gain
    public getPostGainNode():AudioNode { return this.currentGainNode }



    private playOnDevice(buffer: AudioBuffer): void {
        if (this.imSpeaking || this.isStopped || this.audioContext.state === 'closed') {
            return;
        }

        this.imSpeaking = true;

        this.currentGainNode.gain.setValueAtTime(this.gain, this.audioContext.currentTime);

        this.audioBufferSourceNode = this.audioContext.createBufferSource();
        this.audioBufferSourceNode.buffer = buffer;
        this.audioBufferSourceNode.onended = () => {
            try {
                this.audioBufferSourceNode?.disconnect();
            } catch {
            }
            // Keep gain-node outputs connected (destination + any analyzer taps).
            // Disconnecting the gain node here can break output VU meters after
            // device reconnect / stream lifecycle changes.
            this.audioBufferSourceNode = null;
            this.imSpeaking = false;
        };

        this.audioBufferSourceNode.connect(this.currentGainNode);
        this.currentGainNode.connect(this.audioContext.destination);
        this.audioBufferSourceNode.start();
    }
}